import React, { useState } from 'react';
import { Button, Checkbox, Modal } from 'ncoded-component-library';
import { unmountComponentAtNode, render } from 'react-dom';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import './ConfirmModal.styles.scss';
import './ConfirmModal.styles.responsive.scss';

type ConfirmConfig = {
  className?: string;
  type?: 'confirm' | 'delete';
  title?: string;
  content?: React.ReactNode;
  submitBtnText?: string;
  cancelBtnText?: string;
  onSubmit?: () => void;
  onClose?: () => void;
  yesNoQuestion?: boolean;
  overlayCloses?: boolean;
  copyEnum?: [string, string];
  onlyOneOption?: boolean;
  checkboxAgreeLabel?: string;
};

export type ConfirmObject = {
  simpleConfirm: (conf: ConfirmConfig) => Promise<boolean>;
};

const ConfirmModal: React.FC<ConfirmConfig> = (props) => {
  const {
    type = 'confirm',
    className,
    title,
    content,
    submitBtnText,
    cancelBtnText,
    onClose,
    onSubmit,
    yesNoQuestion,
    checkboxAgreeLabel,
    copyEnum = ['', ''],
    onlyOneOption = false,
    ...rest
  } = props;
  const { t } = useTranslation();
  const [yesKey, noKey] = copyEnum;
  const [checkboxValue, setCheckboxValue] = useState(false);

  const classes = classNames(
    'confirm-modal',
    `confirm-modal--${type}`,
    className,
  );

  return (
    <Modal
      className={classes}
      footer={
        <>
          {!onlyOneOption && (
            <Button styleType="secondary" onClick={onClose}>
              {cancelBtnText || t(noKey || (yesNoQuestion ? 'no' : 'cancel'))}
            </Button>
          )}
          <Button
            styleType={type === 'delete' ? 'secondary' : 'primary'}
            className={classNames({ error: type === 'delete' })}
            onClick={onSubmit}
            disabled={checkboxAgreeLabel && !checkboxValue}
          >
            {submitBtnText || t(yesKey || (yesNoQuestion ? 'yes' : 'ok'))}
          </Button>
        </>
      }
      open
      onClose={onClose}
      {...rest}
    >
      <p className="conf-modal-title">{title}</p>
      {content}
      {checkboxAgreeLabel && (
        <Checkbox
          label={checkboxAgreeLabel}
          checked={checkboxValue}
          onChange={() => setCheckboxValue((prev) => !prev)}
        />
      )}
    </Modal>
  );
};

export default async (config: ConfirmConfig, yesNoQuestion = false) => {
  return new Promise((resolve) => {
    const { title, content, ...rest } = config;

    const div = document.createElement('div');
    document.body.appendChild(div);

    const cleanup = () => {
      unmountComponentAtNode(div);
      document.body.removeChild(div);
    };

    // eslint-disable-next-line react/no-render-return-value
    return render(
      <ConfirmModal
        title={title}
        content={content}
        onSubmit={() => {
          resolve(true);
          cleanup();
        }}
        onClose={() => {
          resolve(false);
          cleanup();
        }}
        {...rest}
        yesNoQuestion={yesNoQuestion}
      />,
      div,
    );
  });
};
